import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { isAuthenticated, getCurrentUser } from '../services/auth';
import { UserPlus, Mail } from 'lucide-react';
import api from '../services/api';
import VerifyEmailModal from '../components/VerifyEmailModal';

const USER_GUIDE_URL = `${process.env.PUBLIC_URL}/images/Stonewall-Invoicing-User-Guide.pdf`;

const Downloads = () => {
  const { darkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [showAuthMessage, setShowAuthMessage] = useState(false);
  const [downloadingWindows, setDownloadingWindows] = useState(false);
  const [downloadInfo, setDownloadInfo] = useState({ version: null, releaseDate: null, fileSize: null });
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const redirectTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const response = await api.get('/download/info');
        setDownloadInfo(response.data);
      } catch (err) {
        console.error('Failed to fetch download info:', err);
      }
    };
    fetchInfo();
  }, []);

  const user = getCurrentUser();
  const isVerified = user?.IsEmailVerified === true || user?.isEmailVerified === true;

  const handleDownload = async (platform) => {
    if (!isAuthenticated()) {
      setShowAuthMessage(true);
      redirectTimeoutRef.current = setTimeout(() => {
        navigate('/login');
      }, 2000);
    } else {
      if (platform !== 'windows') return;

      try {
        setDownloadingWindows(true);
        const endpoint = platform === 'windows' ? '/download/windows' : '/download/mac';
        const response = await api.get(endpoint, { responseType: 'blob' });

        let fileName = platform === 'windows' ? 'TorqueInstaller.exe' : 'TorqueInstaller.dmg';
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1].replace(/['"]/g, '');
          }
        }

        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setShowUserGuideModal(true);
      } catch (error) {
        console.error('Download failed:', error);
        alert('Download failed. Please try again or contact support.');
      } finally {
        setDownloadingWindows(false);
      }
    }
  };

  return (
    <div className={`flex-1 flex flex-col ${darkMode ? 'dark' : ''} bg-white dark:bg-gray-900`}>
      <div className="flex-grow">

        {/* Header */}
        <div className="text-center pt-16 pb-10 px-4">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white tracking-tight mb-4">
            Get Stonewall Invoicing.
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
            Download the latest version for your operating system.
          </p>
        </div>

        {/* Auth Message */}
        {showAuthMessage && (
          <div className="max-w-3xl mx-auto mb-6 px-4">
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-400 px-5 py-4 rounded-2xl flex items-center">
              <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-sm font-medium">
                An account is required to download. Redirecting to login...
              </p>
            </div>
          </div>
        )}

        <div className="max-w-5xl mx-auto px-4 pb-20">

          {/* Download Cards */}
          <div className="grid gap-6 md:grid-cols-2 mb-8">

            {/* Windows */}
            <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-lg p-8">
              <div className="flex items-center mb-5">
                <svg className="w-8 h-8 text-blue-500 mr-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M0,0H11.377V11.372H0ZM12.623,0H24V11.372H12.623ZM0,12.623H11.377V24H0Zm12.623,0H24V24H12.623" />
                </svg>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Windows</h2>
              </div>
              <p className="text-gray-500 dark:text-gray-400 mb-5">Compatible with Windows 10 and Windows 11</p>
              <div className="space-y-2 mb-6 text-sm text-gray-400 dark:text-gray-500">
                <div className="flex justify-between"><span>Version</span><span className="text-gray-700 dark:text-gray-300">{downloadInfo.version || '\u2014'}</span></div>
                <div className="flex justify-between"><span>Released</span><span className="text-gray-700 dark:text-gray-300">{downloadInfo.releaseDate ? new Date(downloadInfo.releaseDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '\u2014'}</span></div>
                <div className="flex justify-between"><span>Size</span><span className="text-gray-700 dark:text-gray-300">{downloadInfo.fileSize || '\u2014'}</span></div>
              </div>
              <button
                onClick={() => {
                  if (!isAuthenticated()) {
                    handleDownload('windows');
                  } else if (!isVerified) {
                    setShowVerifyModal(true);
                  } else {
                    handleDownload('windows');
                  }
                }}
                disabled={downloadingWindows || !isAuthenticated()}
                className={`w-full py-3 rounded-lg text-base font-semibold flex items-center justify-center transition-colors ${
                  downloadingWindows
                    ? 'bg-gray-400 cursor-not-allowed text-white'
                    : !isAuthenticated()
                      ? 'bg-amber-600 cursor-not-allowed text-black'
                      : !isVerified
                        ? 'bg-amber-600 hover:bg-amber-700 text-black cursor-pointer'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {downloadingWindows ? (
                  <>
                    <svg className="w-5 h-5 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Downloading...
                  </>
                ) : !isAuthenticated() ? (
                  <><UserPlus className="w-5 h-5 mr-2" />Create Account to Download</>
                ) : !isVerified ? (
                  <><Mail className="w-5 h-5 mr-2" />Verify Email to Download</>
                ) : (
                  'Download for Windows'
                )}
              </button>
            </div>

            {/* macOS */}
            <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-lg p-8">
              <div className="flex items-center mb-5">
                <svg className="w-8 h-8 text-gray-900 dark:text-white mr-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                </svg>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">macOS</h2>
              </div>
              <p className="text-gray-500 dark:text-gray-400 mb-5">Compatible with macOS 10.14 and later</p>
              <div className="space-y-2 mb-6 text-sm text-gray-400 dark:text-gray-500">
                <div className="flex justify-between"><span>Version</span><span>{'\u2014'}</span></div>
                <div className="flex justify-between"><span>Released</span><span>{'\u2014'}</span></div>
                <div className="flex justify-between"><span>Size</span><span>{'\u2014'}</span></div>
              </div>
              <button disabled className="w-full py-3 rounded-lg text-base font-semibold bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed">
                Coming Soon
              </button>
            </div>
          </div>

          {/* System Requirements */}
          <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-lg p-8 md:p-12 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">System Requirements</h2>
            <ul className="space-y-3 text-gray-500 dark:text-gray-400">
              {[
                'Windows 10 (64-bit) or Windows 11',
                '4 GB RAM (8 GB recommended)',
                '1 GB available disk space',
                '1280 x 768 screen resolution',
                'Internet connection for updates'
              ].map((req) => (
                <li key={req} className="flex items-center">
                  <svg className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {req}
                </li>
              ))}
            </ul>
          </div>

          {/* Help CTA */}
          <div className="bg-gray-900 dark:bg-[#111] rounded-lg p-8 md:p-12 text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <h2 className="text-2xl font-bold mb-2">Need help getting started?</h2>
              <p className="text-gray-400">Check out the documentation and user guide.</p>
            </div>
            <Link to="/documentation"
              className="inline-flex items-center bg-white text-gray-900 hover:bg-gray-100 px-6 py-3 rounded-lg text-base font-semibold transition-colors whitespace-nowrap">
              View Documentation
            </Link>
          </div>

        </div>
      </div>

      <VerifyEmailModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        userEmail={user?.email || user?.Email}
      />

      {/* User Guide Modal */}
      {showUserGuideModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-8">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Download started!</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Would you also like the <strong className="text-gray-900 dark:text-white">User Guide</strong>? It covers installation, setup, and everything you need to get started.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = USER_GUIDE_URL;
                  link.download = 'Stonewall-Invoicing-User-Guide.pdf';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  setShowUserGuideModal(false);
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg text-sm font-semibold transition-colors"
              >
                Download User Guide
              </button>
              <button
                onClick={() => setShowUserGuideModal(false)}
                className="px-5 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-semibold transition-colors"
              >
                No Thanks
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Downloads;
